'use client';

import { Twitter, Linkedin, Link2, Check } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';

interface ShareButtonsProps {
  url: string;
  title: string;
  description?: string;
}

// Shared button styles to avoid duplication
const shareButtonClassName =
  'bg-card/60 backdrop-blur-sm border hover:bg-accent hover:border-blue-500/50 transition-all duration-200 font-medium shadow-sm hover:shadow-md';

export function ShareButtons({ url, title }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  // Initialize with url only to match server-side render, then update after hydration
  const [fullUrl, setFullUrl] = useState(url);

  useEffect(() => {
    // Update to include origin after hydration to avoid hydration mismatch
    setFullUrl(`${window.location.origin}${url}`);
  }, [url]);

  const shareToTwitter = useCallback(() => {
    const text = encodeURIComponent(title);
    const twitterUrl = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(fullUrl)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420,noopener,noreferrer');
  }, [title, fullUrl]);

  const shareToLinkedIn = useCallback(() => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`;
    window.open(linkedInUrl, '_blank', 'width=550,height=420,noopener,noreferrer');
  }, [fullUrl]);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Silently fail - user can still copy manually
      // In production, you might want to use a toast notification here
      if (process.env.NODE_ENV === 'development') {
        logger.warn('Failed to copy to clipboard', { error: err });
      }
    }
  }, [fullUrl]);

  return (
    <div className="mt-12 sm:mt-16 pt-8 sm:pt-12 border-t border-border">
      <div className="flex flex-wrap items-center gap-4 sm:gap-5">
        <span className="text-sm sm:text-base text-muted-foreground font-semibold uppercase tracking-wide">
          Share this article
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={shareToTwitter}
            className={shareButtonClassName}
            aria-label="Share on Twitter"
          >
            <Twitter className="h-4 w-4 mr-2" aria-hidden="true" />
            Twitter
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={shareToLinkedIn}
            className={shareButtonClassName}
            aria-label="Share on LinkedIn"
          >
            <Linkedin className="h-4 w-4 mr-2" aria-hidden="true" />
            LinkedIn
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={copyToClipboard}
            className={shareButtonClassName}
            aria-label={copied ? 'Link copied to clipboard' : 'Copy article link'}
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2" aria-hidden="true" />
                Copied!
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" aria-hidden="true" />
                Copy Link
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
